import {ReactElement, ReactNode, useState} from 'react';
import {Link as RouterLink} from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import {styled} from '@mui/material/styles';

import {CurrencyValue} from '@shared/components';
import {ROUTE__USER_ACCOUNT} from '@shared/constants';
import {
  useGetUser,
  useGetUserCurrency,
  useGetUserExtDataByName,
} from '../../apiHooks';
import {ClickIdValue} from '../ClickIdValue';
import {UserAvatar} from '../UserAvatar';

const EMPTY_VALUE = '—';

const WhiteTooltipArrow = styled('span')({
  overflow: 'hidden',
  position: 'absolute',
  width: '1em',
  height: '0.71em',
  boxSizing: 'border-box',
  color: '#fff',
  '&::before': {
    content: '""',
    margin: 'auto',
    display: 'block',
    width: '100%',
    height: '100%',
    backgroundColor: 'currentColor',
    transform: 'rotate(45deg)',
  },
});

interface UserHoverCardProps {
  userId: string;
  children: ReactElement;
}

interface UserLinkProps {
  userId: string;
}

const PreviewField = ({
  label,
  value,
  monospace = false,
  action,
}: {
  label: string;
  value: ReactNode;
  monospace?: boolean;
  action?: ReactNode;
}) => (
  <>
    <Typography variant="b2regular" color="text.secondary">
      {label}
    </Typography>
    <Box display="flex" minWidth={0} alignItems="center">
      <Typography
        variant="b2regular"
        noWrap
        sx={{
          ...(monospace
            ? {fontFamily: 'monospace', flexShrink: 0}
            : {minWidth: 0}),
        }}
      >
        {value}
      </Typography>
      {action}
    </Box>
  </>
);

const Balance = ({label, value}: {label: string; value: ReactNode}) => (
  <Box display="flex" flexDirection="column" minWidth={0} gap={0.5}>
    <Typography variant="b3regular" color="text.secondary" noWrap>
      {label}
    </Typography>
    <Typography variant="b2medium" noWrap>
      {value}
    </Typography>
  </Box>
);

const UserHoverCardContent = ({userId}: Pick<UserHoverCardProps, 'userId'>) => {
  const {
    data: user,
    isLoading,
    isError,
  } = useGetUser(userId, {
    enabled: Boolean(userId),
  });
  const {data: currency} = useGetUserCurrency(user?.username ?? '', {
    enabled: Boolean(user?.username),
  });
  const {
    data: clickId,
    isFetching: isLoadingClickId,
    isError: isClickIdError,
  } = useGetUserExtDataByName(user?.id ?? '', 'sub1');
  let clickIdValue = clickId?.value || EMPTY_VALUE;
  if (isLoadingClickId) clickIdValue = 'Loading…';
  else if (isClickIdError) clickIdValue = 'Unable to load';

  if (isLoading) {
    return (
      <Box
        display="flex"
        minWidth={320}
        minHeight={160}
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (isError || !user) {
    return (
      <Box minWidth={320} p={2}>
        <Typography variant="b2regular">Unable to load user data</Typography>
      </Box>
    );
  }

  return (
    <Box
      width="fit-content"
      minWidth={544}
      maxWidth="calc(100vw - 32px)"
      px={2}
      pt={2.5}
      pb={2}
    >
      <Box display="flex" gap={2} alignItems="flex-start">
        <UserAvatar
          name={user.name}
          photoUrl={user.avatar || undefined}
          size={56}
        />

        <Box
          display="grid"
          flex={1}
          gridTemplateColumns="120px minmax(0, 1fr)"
          alignItems="center"
          columnGap={1}
          rowGap={0.5}
        >
          <PreviewField
            label="ID"
            value={user.id}
            monospace
            action={
              <Tooltip title="Open user">
                <IconButton
                  aria-label="Open user"
                  component={RouterLink}
                  rel="noopener noreferrer"
                  size="small"
                  sx={{alignSelf: 'center', ml: 1}}
                  target="_blank"
                  to={ROUTE__USER_ACCOUNT.replace(':id', user.id)}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          />
          <PreviewField label="Username" value={`@${user.username}`} />
          <PreviewField
            label="Referred by"
            value={
              user.referrer ? (
                <Link
                  component={RouterLink}
                  to={ROUTE__USER_ACCOUNT.replace(':id', user.referrer.id)}
                >
                  @{user.referrer.username}
                </Link>
              ) : user.referrer === null ? (
                EMPTY_VALUE
              ) : (
                'Unavailable'
              )
            }
          />
          <PreviewField label="Name" value={user.name} />
          <PreviewField label="Auth type" value={user.authType} />
          <PreviewField
            label="Banned"
            value={
              <Chip
                label={user.ban ? 'Yes' : 'No'}
                color={user.ban ? 'error' : 'default'}
                size="small"
              />
            }
          />
          <PreviewField label="Phone" value={user.phone || EMPTY_VALUE} />
          <PreviewField label="Email" value={user.email || EMPTY_VALUE} />
          <PreviewField label="Country" value={user.country || EMPTY_VALUE} />
          <PreviewField
            label="Currency"
            value={currency?.currency ?? EMPTY_VALUE}
          />
          <PreviewField
            label="ClickID"
            value={
              !isLoadingClickId && !isClickIdError && clickId?.value ? (
                <ClickIdValue value={clickId.value} />
              ) : (
                clickIdValue
              )
            }
          />
          <PreviewField
            label="Verified"
            value={
              <Chip
                label={user.isVerified ? 'Yes' : 'No'}
                color={user.isVerified ? 'success' : 'default'}
                size="small"
              />
            }
          />
          <PreviewField
            label="Admin"
            value={
              <Chip
                label={user.isAdmin ? 'Yes' : 'No'}
                color={user.isAdmin ? 'warning' : 'default'}
                size="small"
              />
            }
          />
        </Box>
      </Box>

      <Divider sx={{my: 1.5}} />

      <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={1}>
        <Balance
          label="Real balance"
          value={<CurrencyValue value={user.fundsReal} />}
        />
        <Balance
          label="Promo balance"
          value={<CurrencyValue value={user.fundsPromo} />}
        />
        <Balance label="Coins" value={user.coins} />
        <Balance label="CFP" value={user.cfp} />
      </Box>
    </Box>
  );
};

export const UserHoverCard = ({userId, children}: UserHoverCardProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Tooltip
      arrow
      disableFocusListener
      placement="right-start"
      open={isOpen}
      slots={{arrow: WhiteTooltipArrow}}
      slotProps={{
        popper: {
          sx: {
            '& .MuiTooltip-arrow': {
              filter: 'drop-shadow(-3px 2px 2px rgb(0 0 0 / 20%))',
            },
            '&[data-popper-placement*="right"] .MuiTooltip-arrow': {
              marginLeft: '-8px',
              top: '1px !important',
            },
          },
        },
        tooltip: {
          sx: {
            bgcolor: '#fff',
            border: 1,
            borderColor: 'divider',
            boxShadow: 4,
            color: 'text.primary',
            maxWidth: 'none',
            p: 0,
          },
        },
      }}
      title={isOpen ? <UserHoverCardContent userId={userId} /> : ''}
      onClose={() => setIsOpen(false)}
      onOpen={() => setIsOpen(true)}
    >
      <Box component="span" display="inline-flex">
        {children}
      </Box>
    </Tooltip>
  );
};

export const UserLink = ({userId}: UserLinkProps) => {
  if (!userId) {
    return <>{EMPTY_VALUE}</>;
  }

  return (
    <UserHoverCard userId={userId}>
      <Link
        component={RouterLink}
        to={ROUTE__USER_ACCOUNT.replace(':id', userId)}
        underline="hover"
      >
        {userId.slice(0, 8)}
      </Link>
    </UserHoverCard>
  );
};
